import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";

import { patientsApi } from "@/shared/api/patients";
import { allergySeverityLabel } from "@/shared/format";
import {
  ALLERGY_OVERRIDE_REASONS,
  allergyOverrideReasonLabel,
  type AllergyOverrideReason,
  type AllergyResponse,
  type DrugAllergyWarning,
} from "@/shared/types/patient";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Drug-allergy safety on the prescribing screen (#18859).
 *
 * <p>Two halves, and they answer different failure modes.
 *
 * <ul>
 *   <li>{@link DrugAllergyStrip} — the allergies are ON the screen where the doctor prescribes,
 *       instead of behind a tab in another page. Fixes "nobody looked".
 *   <li>{@link DrugAllergyWarningDialog} — picking a medicine the patient reacts to stops and asks.
 *       Fixes "looked, but did not connect this product to that allergen".
 * </ul>
 *
 * <p>The dialog is a SOFT block: the doctor can proceed, because prescribing despite a recorded
 * allergy is a legitimate clinical decision (mild reaction, desensitised, benefit outweighs risk).
 * A hard block would push people to delete the allergy record to get through, which destroys the
 * data the check depends on.
 *
 * <p><b>Proceeding requires a reason</b> (owner's §5, 09/09). A bare "Confirm" records only that
 * somebody clicked, which answers none of the questions an audit asks afterwards. And the dialog is
 * not the enforcement — the backend refuses to finalize the prescription until the override exists,
 * so a client that skipped this screen entirely is stopped anyway.
 */

/** Active DRUG allergies, most severe first. Empty renders nothing at all. */
export function DrugAllergyStrip({ patientId }: { patientId: string | undefined }) {
  const allergies = useQuery({
    queryKey: ["patient", patientId, "drug-allergies"],
    queryFn: () => patientsApi.listAllergies(patientId!, false),
    enabled: !!patientId,
  });

  const drug = (allergies.data ?? []).filter((a: AllergyResponse) => a.category === "DRUG");
  if (drug.length === 0) {
    // Deliberately nothing — not "Không có dị ứng". A green all-clear would be a claim about the
    // patient that an empty table cannot support: nobody may have asked them yet.
    return null;
  }

  return (
    <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-destructive">
        <AlertTriangle className="size-4" />
        Dị ứng thuốc
      </div>
      <ul className="mt-1 space-y-0.5 text-sm">
        {drug.map((a) => (
          <li key={a.id} className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium">{a.substance}</span>
            <span
              className={
                a.severity === "SEVERE" || a.severity === "ANAPHYLAXIS"
                  ? "text-xs font-semibold text-destructive"
                  : "text-xs text-muted-foreground"
              }
            >
              {allergySeverityLabel[a.severity]}
            </span>
            {a.reaction && (
              <span className="text-xs text-muted-foreground">— {a.reaction}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

const MATCHED_ON_LABEL: Record<DrugAllergyWarning["matchedOn"], string> = {
  CODE: "trùng mã thuốc",
  ACTIVE_INGREDIENT: "trùng hoạt chất",
  NAME: "trùng tên thuốc",
};

/**
 * Shown when a picked medicine matches a recorded allergy. `onConfirm` prescribes anyway.
 *
 * <p>Says WHY it matched. A warning the doctor cannot evaluate is one they dismiss by reflex;
 * "trùng hoạt chất" and "trùng tên thuốc" are very different levels of evidence.
 */
export function DrugAllergyWarningDialog({
  open,
  medicineName,
  warnings,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  medicineName: string;
  warnings: DrugAllergyWarning[];
  onCancel: () => void;
  onConfirm: (reasonCode: AllergyOverrideReason, reasonText: string | null) => void;
}) {
  const severe = warnings.some((w) => w.severe);
  const [reason, setReason] = useState<AllergyOverrideReason | null>(null);
  const [text, setText] = useState("");

  // Fresh decision every time. Carrying the previous answer over would let a doctor prescribe a
  // second drug on a reason they gave about a different one.
  useEffect(() => {
    if (open) {
      setReason(null);
      setText("");
    }
  }, [open, medicineName]);

  const needsText = reason === "OTHER";
  const canProceed = reason !== null && (!needsText || text.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="size-5" />
            {severe ? "Cảnh báo dị ứng NẶNG" : "Cảnh báo dị ứng"}
          </DialogTitle>
          <DialogDescription>
            Bệnh nhân có tiền sử dị ứng với thuốc vừa chọn —{" "}
            <span className="font-medium text-foreground">{medicineName}</span>.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-2 text-sm">
          {warnings.map((w) => (
            <li key={w.allergyId} className="rounded border border-destructive/30 px-3 py-2">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-semibold">{w.substance}</span>
                <span
                  className={
                    w.severe ? "text-xs font-semibold text-destructive" : "text-xs text-muted-foreground"
                  }
                >
                  {allergySeverityLabel[w.severity]}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({MATCHED_ON_LABEL[w.matchedOn]})
                </span>
              </div>
              {w.reaction && (
                <div className="mt-0.5 text-xs text-muted-foreground">Phản ứng: {w.reaction}</div>
              )}
            </li>
          ))}
        </ul>

        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Lý do vẫn kê thuốc này <span className="text-destructive">*</span>
          </Label>
          <div className="space-y-1.5">
            {ALLERGY_OVERRIDE_REASONS.map((r) => (
              <label
                key={r}
                className="flex cursor-pointer items-start gap-2 text-sm"
                htmlFor={`allergy-reason-${r}`}
              >
                <input
                  id={`allergy-reason-${r}`}
                  type="radio"
                  name="allergy-override-reason"
                  className="mt-0.5"
                  checked={reason === r}
                  onChange={() => setReason(r)}
                />
                <span>{allergyOverrideReasonLabel[r]}</span>
              </label>
            ))}
          </div>
          {needsText && (
            <Textarea
              aria-label="Ghi rõ lý do"
              placeholder="Ghi rõ lý do…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={2}
            />
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Không kê thuốc này
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!canProceed}
            onClick={() => reason && onConfirm(reason, needsText ? text.trim() : null)}
          >
            Vẫn kê
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
